import { useState, useEffect, useCallback } from "react";
import { COLORS, MONO_FONT } from "../styles/theme";
import * as api from "../engine/api";
import useResendCooldown from "../hooks/useResendCooldown";

/**
 * VerifyEmail — the magic-link landing view (L01 FR8).
 *
 * The Console has NO router; App.jsx routes to this view when the browser lands
 * on `/verify-email?token=<raw>` (see hooks/useAuth.js). On mount it calls
 * api.verifyEmail(token): on success the JWT is stored and `onVerified` hands
 * control back to useAuth's logged-in flow (auto-login on verify). On failure
 * (400 invalid/expired, 410 already-used, or a missing token) it shows a clear
 * message plus a resend affordance (a username + resend button, since a failed
 * token tells us nothing about which account to re-send to).
 *
 * Chrome/tokens mirror RegisterScreen so the verification flow reads as one
 * continuous experience. Color semantics: accent = interactive only, ok(green)
 * = the verified/success state only, warn(amber) = the soft failure notice
 * (never red — an expired link is a recoverable caution, not a danger).
 */

const inputStyle = {
  width: "100%", padding: "5px 8px", marginTop: 2, borderRadius: 3,
  border: `1px solid ${COLORS.border}`, background: COLORS.panel,
  color: COLORS.text, fontSize: 14, fontFamily: MONO_FONT,
  outline: "none", boxSizing: "border-box",
};

const labelStyle = { fontSize: 12, color: COLORS.textDim, textTransform: "uppercase" };

const linkButtonStyle = {
  width: "100%", padding: 5, marginTop: 6, borderRadius: 3,
  border: `1px solid ${COLORS.border}`, background: "transparent",
  color: COLORS.textDim, fontSize: 13, fontFamily: MONO_FONT,
  cursor: "pointer", textTransform: "uppercase",
};

/**
 * Map a verify-email failure to a plain, resend-oriented message. OMEGA-590 —
 * the recoverable cases are keyed off the stable error `code` on the new
 * envelope (`token_already_used` / `invalid_or_expired_token`), which replaced
 * the pre-cutover `detail` strings. Anything else falls back to the envelope's
 * human `message`.
 */
function friendlyError(err) {
  const code = err?.code;
  if (code === "token_already_used") {
    return "This verification link has already been used. If you have not signed in yet, request a fresh link below.";
  }
  if (code === "invalid_or_expired_token") {
    return "This verification link is invalid or has expired. Request a fresh link below.";
  }
  return err?.message || "We could not verify your email. Request a fresh link below.";
}

export default function VerifyEmail({ token, onVerified, onBackToLogin }) {
  // status: "verifying" | "success" | "error"
  const [status, setStatus] = useState("verifying");
  const [error, setError] = useState("");
  const [resendUser, setResendUser] = useState("");
  const [resendNotice, setResendNotice] = useState("");
  const [resending, setResending] = useState(false);
  // OMEGA-563 — lock the resend button for the backend cooldown window after a
  // successful send so it stops inviting clicks the server would silently drop.
  const { remaining, onCooldown, start: startCooldown } = useResendCooldown();

  useEffect(() => {
    let cancelled = false;

    if (!token) {
      setStatus("error");
      setError("This verification link is missing its token. Request a fresh link below.");
      return;
    }

    (async () => {
      try {
        const resp = await api.verifyEmail(token);
        if (cancelled) return;
        setStatus("success");
        // Hand control back to useAuth's logged-in flow (auto-login on verify).
        onVerified(resp);
      } catch (err) {
        if (cancelled) return;
        setStatus("error");
        setError(friendlyError(err));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, onVerified]);

  const resend = useCallback(async () => {
    if (!resendUser || resending || onCooldown) return;
    setResending(true);
    setResendNotice("");
    try {
      await api.resendVerification(resendUser);
      // Always 202 (no account enumeration) — one neutral confirmation.
      setResendNotice("If that account exists and awaits verification, a fresh link is on its way. Check your inbox.");
      startCooldown();
    } catch (err) {
      setResendNotice(err.message || "Could not resend the verification email.");
    } finally {
      setResending(false);
    }
  }, [resendUser, resending, onCooldown, startCooldown]);

  return (
    <div style={{
      minHeight: "100vh", background: COLORS.bg,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: MONO_FONT,
    }}>
      <div style={{
        width: 460, background: COLORS.panel, border: `1px solid ${COLORS.border}`,
        borderRadius: 6, overflow: "hidden",
      }}>
        <div style={{ padding: "10px 14px", borderBottom: `1px solid ${COLORS.border}`, textAlign: "center" }}>
          <div style={{ fontSize: 12, color: COLORS.textDim, letterSpacing: ".2em", textTransform: "uppercase" }}>
            Omega AI HPC | theomega.ai
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.textBr, marginTop: 2 }}>
            EMAIL VERIFICATION
          </div>
        </div>

        <div style={{ padding: 14 }}>
          {status === "verifying" && (
            <div data-testid="verify-pending" style={{ fontSize: 13, color: COLORS.textDim, lineHeight: 1.6 }}>
              Verifying your email and signing you in...
            </div>
          )}

          {status === "success" && (
            <div data-testid="verify-success" style={{ fontSize: 13, color: COLORS.ok, lineHeight: 1.6 }}>
              Email verified. Signing you in...
            </div>
          )}

          {status === "error" && (
            <div>
              <div data-testid="verify-error" style={{ fontSize: 13, color: COLORS.warn, marginBottom: 10, lineHeight: 1.6 }}>
                {error}
              </div>

              <div style={{ marginBottom: 8 }}>
                {/* OMEGA-814 — the resend is keyed on the login identity, which is the
                    email. autoComplete stays "username" for the same reason as the login
                    screen: it is the token password managers match a stored sign-in
                    credential on, whatever form that credential takes. */}
                <label style={labelStyle}>Email</label>
                <input
                  value={resendUser}
                  onChange={(e) => setResendUser(e.target.value)}
                  autoComplete="username"
                  onKeyDown={(e) => e.key === "Enter" && resend()}
                  style={inputStyle}
                />
              </div>

              {resendNotice && (
                <div data-testid="verify-resend-notice" style={{ fontSize: 13, color: COLORS.textDim, marginBottom: 8, lineHeight: 1.6 }}>
                  {resendNotice}
                </div>
              )}

              <button
                onClick={resend}
                disabled={!resendUser || resending || onCooldown}
                data-testid="verify-resend"
                style={{
                  width: "100%", padding: 7, borderRadius: 3,
                  border: `1px solid ${COLORS.accent}`, background: COLORS.accentDim,
                  color: COLORS.accent, fontSize: 13, fontFamily: MONO_FONT,
                  fontWeight: 700, cursor: !resendUser || resending || onCooldown ? "not-allowed" : "pointer",
                  textTransform: "uppercase", opacity: !resendUser || resending || onCooldown ? 0.6 : 1,
                }}
              >
                {resending
                  ? "Sending..."
                  : onCooldown
                    ? `Resend available in ${remaining}s`
                    : "Resend verification email"}
              </button>

              <button onClick={onBackToLogin} style={linkButtonStyle}>
                Back to login
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
